/**
 * Category-preference input (M12, FR-1.3/9.6) — a player's single lobby goal:
 * submit/update the categories they'd like to play, plus (for guests) an optional
 * self-reported age band.
 *
 * One control, one command: submitting sends `set_category_preferences`, which is
 * idempotent per player server-side (re-submitting REPLACES the prior submission —
 * M12 §4.1). The form seeds from the player's own current submission so an update
 * shows what they already asked for. Categories are entered as a comma/newline list
 * and normalized to a trimmed, de-duplicated `string[]`.
 *
 * The age band is this-game-only and never persisted; it's offered only to guests
 * (registered players' bands are derived server-side from their profile — M12 §6.2).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AgeBand } from '@jeopardy/shared';
import {
  fetchCategorySuggestions,
  MAX_SUGGESTION_EXCLUSIONS,
} from '../net/suggestions-api';

const AGE_BANDS: readonly AgeBand[] = ['KID', 'TEEN', 'ADULT'];
const AGE_BAND_LABELS: Record<AgeBand, string> = {
  KID: 'Kid',
  TEEN: 'Teen',
  ADULT: 'Adult',
};

interface CategoryPreferencesProps {
  /** This player's already-submitted categories (to seed an update). */
  initialCategories?: string[];
  /** This player's already-submitted band, if a guest previously chose one. */
  initialAgeBand?: AgeBand;
  /** Offer the guest age-band select (registered players omit it — M12 §6.2). */
  allowAgeBand?: boolean;
  /** Fire the (idempotent) `set_category_preferences` command with the parsed input. */
  onSubmit: (categories: string[], ageBand?: AgeBand) => void;
  /**
   * The game id. When set (a dynamic prod lobby), history-aware suggestion chips are
   * fetched and shown (issue #37, FR-10.4). Absent → no chips (plain manual entry).
   */
  gameId?: string;
  /**
   * Injectable suggestions fetcher (tests); defaults to the real control-plane read.
   * `exclude` carries the categories already shown this session + what's typed so a
   * refresh returns genuinely new ones (issue #61).
   */
  fetchSuggestions?: (gameId: string, exclude: string[]) => Promise<string[]>;
}

/**
 * Build the exclusion list for a suggestion request (issue #61): everything already
 * surfaced to the player this session, plus what they've currently typed, so a refresh
 * never repeats a shown category and never re-suggests a category the player just
 * entered. De-duplicated case-insensitively, most-recent-first, capped to bound the
 * query string. Exported for unit testing.
 */
export function buildSuggestionExclusions(
  shown: readonly string[],
  typed: readonly string[],
  cap = MAX_SUGGESTION_EXCLUSIONS,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  // Typed categories first (always exclude what the player just entered), then the
  // most-recently-shown suggestions (shown is oldest-first, so iterate it reversed).
  for (const c of [...typed, ...[...shown].reverse()]) {
    const cat = c.trim();
    const key = cat.toLowerCase();
    if (key.length === 0 || seen.has(key)) continue;
    seen.add(key);
    out.push(cat);
    if (out.length >= cap) break;
  }
  return out;
}

/** Split a free-text list on commas/newlines → trimmed, de-duplicated categories. */
export function parseCategories(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,\n]/)) {
    const cat = part.trim();
    if (cat.length === 0) continue;
    const key = cat.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cat);
  }
  return out;
}

export function CategoryPreferences({
  initialCategories,
  initialAgeBand,
  allowAgeBand,
  onSubmit,
  gameId,
  fetchSuggestions,
}: CategoryPreferencesProps): React.JSX.Element {
  const [text, setText] = useState(() => (initialCategories ?? []).join(', '));
  const [ageBand, setAgeBand] = useState<AgeBand | ''>(initialAgeBand ?? '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // Gate the refresh button until the initial load resolves (issue #61, D61.5) —
  // distinct from the transient `loadingSuggestions` so there's nothing to "refresh"
  // before the first list arrives.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const categories = useMemo(() => parseCategories(text), [text]);
  const hasSubmitted = (initialCategories?.length ?? 0) > 0;
  const canSubmit = categories.length > 0;

  // History-aware suggestion chips (FR-10.4): fetch on mount + on explicit refresh.
  // Best-effort — a failure/empty just hides the chips; it never blocks manual entry.
  const fetcher = fetchSuggestions ?? fetchCategorySuggestions;
  // Every suggestion shown this session (issue #61), oldest-first, deduped by a
  // normalized key — sent as exclusions so a refresh never repeats one. Kept in refs
  // (not deps) so `loadSuggestions` stays stable and the mount effect fires once;
  // otherwise it would re-fetch on every keystroke and spam the throttled read.
  const shownRef = useRef<string[]>([]);
  const shownKeysRef = useRef<Set<string>>(new Set());
  const categoriesRef = useRef<string[]>(categories);
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  const loadSuggestions = useCallback(async () => {
    if (!gameId) return;
    setLoadingSuggestions(true);
    try {
      const exclude = buildSuggestionExclusions(shownRef.current, categoriesRef.current);
      const next = await fetcher(gameId, exclude);
      // Remember everything we've now shown so the next refresh excludes it too.
      for (const s of next) {
        const key = s.trim().toLowerCase();
        if (key.length === 0 || shownKeysRef.current.has(key)) continue;
        shownKeysRef.current.add(key);
        shownRef.current.push(s.trim());
      }
      setSuggestions(next);
    } finally {
      setLoadingSuggestions(false);
      setHasLoadedOnce(true);
    }
  }, [gameId, fetcher]);

  useEffect(() => {
    void loadSuggestions();
  }, [loadSuggestions]);

  // Only offer chips the player hasn't already added (case-insensitive).
  const chosen = new Set(categories.map((c) => c.toLowerCase()));
  const openSuggestions = suggestions.filter((s) => !chosen.has(s.toLowerCase()));

  /** Append a tapped suggestion, re-parsing so the trim/dedupe/cap rules still hold. */
  const addCategory = (cat: string): void => {
    setText((prev) => parseCategories(`${prev}, ${cat}`).join(', '));
  };

  return (
    <form
      className="category-prefs"
      data-testid="category-preferences"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit(categories, ageBand === '' ? undefined : ageBand);
      }}
    >
      <label className="category-prefs__field">
        <span>What would you like to play? (comma-separated categories)</span>
        <textarea
          data-testid="category-prefs-input"
          aria-label="Wished categories"
          placeholder="World Capitals, 90s Movies, Marine Biology…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
        />
      </label>

      {/* History-aware suggestion chips (issue #37, FR-10.4): categories the seated
          group hasn't recently played. Tap to add; "Suggest more" requests a fresh set
          that excludes everything already shown this session (issue #61). The refresh
          button appears only after the initial list loads (D61.5). Only shown for a
          dynamic prod lobby (gameId set). */}
      {gameId && (
        <div
          className="category-prefs__suggestions"
          data-testid="category-prefs-suggestions"
        >
          <div className="category-prefs__suggestions-head">
            <span className="muted">Suggested for your group — tap to add</span>
            {hasLoadedOnce && (
              <button
                type="button"
                className="btn btn--ghost"
                data-testid="category-prefs-refresh"
                onClick={() => void loadSuggestions()}
                disabled={loadingSuggestions}
              >
                Suggest more
              </button>
            )}
          </div>
          {loadingSuggestions ? (
            <p className="muted" data-testid="category-prefs-suggestions-loading">
              Finding fresh ideas…
            </p>
          ) : openSuggestions.length > 0 ? (
            <ul className="category-prefs__chips">
              {openSuggestions.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    className="chip"
                    data-testid="category-suggestion-chip"
                    onClick={() => addCategory(s)}
                  >
                    + {s}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted" data-testid="category-prefs-suggestions-empty">
              No fresh suggestions right now — type your own above.
            </p>
          )}
        </div>
      )}

      {categories.length > 0 && (
        <ul className="category-prefs__preview" data-testid="category-prefs-preview">
          {categories.map((c, i) => (
            <li key={`${c}-${i}`}>{c}</li>
          ))}
        </ul>
      )}

      {allowAgeBand && (
        <label className="category-prefs__field">
          <span>Your age group (optional — helps pick difficulty)</span>
          <select
            data-testid="category-prefs-age"
            aria-label="Age group"
            value={ageBand}
            onChange={(e) => setAgeBand(e.target.value as AgeBand | '')}
          >
            <option value="">Prefer not to say</option>
            {AGE_BANDS.map((b) => (
              <option value={b} key={b}>
                {AGE_BAND_LABELS[b]}
              </option>
            ))}
          </select>
        </label>
      )}

      <button
        type="submit"
        className="btn btn--primary"
        data-testid="category-prefs-submit"
        disabled={!canSubmit}
      >
        {hasSubmitted ? 'Update my categories' : 'Submit my categories'}
      </button>
    </form>
  );
}
